import React, { useEffect, useRef, useState } from 'react'
import { X, Upload, AlertTriangle, CheckCircle, Ship, Database } from 'lucide-react'
import { projectsApi, ParseAssessmentResultNew, ParseAssessmentResultLegacy } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { vaiReplaceSummary } from '@/lib/vaiSummary'
import { Button } from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'

interface Props {
  projectSystemId: string
  initialFile?: File | null
  onClose: () => void
  onSuccess: () => void
  // If a legacy IACS Rec.171 workbook is uploaded into this modal, the
  // backend dispatches and returns format='legacy'. We can't render the
  // legacy flow here, so we hand the result to the parent which opens
  // ImportLegacyRaModal and we close.
  onLegacyDetected?: (result: ParseAssessmentResultLegacy) => void
}

export const ImportAssessmentModal: React.FC<Props> = ({ projectSystemId, initialFile, onClose, onSuccess, onLegacyDetected }) => {
  const { t } = useTranslation()
  const { error: showError, success } = useToast()
  const [file, setFile] = useState<File | null>(null)
  const [parseResult, setParseResult] = useState<ParseAssessmentResultNew | null>(null)
  const [parsing, setParsing] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [reuseChoice, setReuseChoice] = useState<'reuse' | 'new' | null>(null)
  const [showChoiceOverride, setShowChoiceOverride] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const initialParseStarted = useRef(false)

  const handleFileChange = async (f: File | null) => {
    setFile(f)
    setParseResult(null)
    setReuseChoice(null)
    if (!f) return

    setParsing(true)
    try {
      const result = await projectsApi.parseAssessment(projectSystemId, f)
      if (result.format === 'legacy') {
        if (onLegacyDetected) {
          onLegacyDetected(result)
          onClose()
        } else {
          showError('Legacy workbook detected. Close and drop it onto the page to use the legacy import flow.')
          setFile(null)
        }
        return
      }
      setParseResult(result)
      // auto-decide: identical content (or an empty profile) → reuse,
      // differing content → separate variant. no question by default;
      // a "Change" toggle keeps the manual override reachable.
      setReuseChoice(result.existing_profile ? (result.existing_profile.auto_reuse ? 'reuse' : 'new') : 'new')
      setShowChoiceOverride(false)
    } catch (err: any) {
      showError(err.message ?? t('importModal.parseFailed'))
      setFile(null)
    } finally {
      setParsing(false)
    }
  }

  useEffect(() => {
    if (initialFile && !initialParseStarted.current) {
      initialParseStarted.current = true
      void handleFileChange(initialFile)
    }
    // The parent keys the modal by route/file; parse an initial drop once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCommit = async () => {
    if (!parseResult || !file || !reuseChoice) return
    setCommitting(true)
    try {
      const result = await projectsApi.commitAssessment(projectSystemId, {
        use_existing_profile_id: reuseChoice === 'reuse' ? parseResult.existing_profile?.id : undefined,
        create_variant: reuseChoice === 'new' && !!parseResult.existing_profile,
        parsed: parseResult.parsed,
        proposed_profile: parseResult.proposed_profile,
        source_filename: file.name,
        vai_rows:          parseResult.vai_preview?.rows,
        vai_vessel_header: parseResult.vai_preview?.vessel_header,
      })
      const vaiDeleted  = result.vai_deleted  ?? 0
      const vaiInserted = result.vai_inserted ?? 0
      const vaiSkipped  = result.vai_skipped  ?? 0
      if (vaiDeleted > 0 || vaiInserted > 0 || vaiSkipped > 0) {
        success(`Risk assessment saved. Vessel Asset Inventory: ${vaiReplaceSummary(vaiDeleted, vaiInserted, vaiSkipped)}.`)
      } else {
        success(t('importModal.raSaved'))
      }
      onSuccess()
    } catch (err: any) {
      showError(err.message ?? t('importModal.saveAssessmentFailed'))
    } finally {
      setCommitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold">{t('systemDetail.importRa')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!parseResult && (
            <div>
              <div
                onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true) }}
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true) }}
                onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false) }}
                onDrop={e => {
                  e.preventDefault(); e.stopPropagation(); setDragActive(false)
                  const f = e.dataTransfer.files?.[0]
                  if (f) handleFileChange(f)
                }}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-slate-300 dark:border-slate-600 hover:border-blue-400'
                } ${parsing ? 'opacity-60 pointer-events-none' : ''}`}
                onClick={() => document.getElementById('assessment-file-input')?.click()}
              >
                <Upload className={`h-8 w-8 mx-auto mb-2 ${dragActive ? 'text-blue-500' : 'text-slate-400'}`} />
                <div className="text-sm font-medium">
                  {parsing ? t('importModal.parsing') : file ? file.name : dragActive ? t('importModal.dropHere') : t('importModal.dropRaWorkbook')}
                </div>
                <div className="text-xs text-slate-400 mt-1">.xlsx file with sheets 01 (CBS) and 02 (Vendor Response)</div>
                <input
                  id="assessment-file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={e => handleFileChange(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>
          )}

          {parseResult && (
            <>
              {/* CBS result */}
              <div className={`p-3 rounded-lg ${parseResult.parsed.is_cbs ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200' : 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200'}`}>
                <div className="flex items-center gap-2">
                  {parseResult.parsed.is_cbs ? (
                    <><AlertTriangle className="h-4 w-4 text-amber-600" /> <span className="text-sm font-medium">{t('importModal.classifiedCbs')}</span></>
                  ) : (
                    <><CheckCircle className="h-4 w-4 text-emerald-600" /> <span className="text-sm font-medium">{t('importModal.notCbs')}</span></>
                  )}
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                  Answers: Q1={parseResult.parsed.cbs_answers.q1 ?? '—'},
                  Q2={parseResult.parsed.cbs_answers.q2 ?? '—'},
                  Q3={parseResult.parsed.cbs_answers.q3 ?? '—'},
                  Q4={parseResult.parsed.cbs_answers.q4 ?? '—'},
                  Q5={parseResult.parsed.cbs_answers.q5 ?? '—'},
                  Q6={parseResult.parsed.cbs_answers.q6 ?? '—'}
                </div>
                {parseResult.parsed.is_cbs && (
                  <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                    {parseResult.parsed.vendor_responses.length} vendor response questions parsed
                  </div>
                )}
              </div>

              {/* Profile */}
              <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
                <div className="text-xs text-slate-400 uppercase mb-2">System profile</div>
                <div className="text-sm font-medium">{parseResult.proposed_profile.vendor_name}</div>
                <div className="text-xs text-slate-500">{parseResult.proposed_profile.model_name}</div>
              </div>

              {/* VAI sheet 3 preview / warning */}
              {parseResult.vai_preview && parseResult.vai_preview.row_count > 0 && (
                <div className="p-3 border border-sky-200 bg-sky-50 dark:bg-sky-900/20 dark:border-sky-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4 text-sky-600" />
                    <span className="text-sm font-medium text-sky-800 dark:text-sky-300">
                      Vessel Asset Inventory detected
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                    {parseResult.vai_preview.row_count} inventory row{parseResult.vai_preview.row_count !== 1 ? 's' : ''} from sheet 3 will be added to the project on save. Rows that match an existing
                    (project, item number) pair are skipped automatically.
                  </div>
                </div>
              )}
              {parseResult.vai_parse_error && (
                <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-500 dark:text-slate-400">
                  Sheet 3 (Vessel Asset Inventory) not imported: {parseResult.vai_parse_error}
                </div>
              )}
              {/* QA item 1 (2026-06-12): close the silent sheet-found-
                  but-zero-rows path — see ImportLegacyRaModal for the
                  same banner. */}
              {parseResult.vai_preview && parseResult.vai_preview.row_count === 0 && !parseResult.vai_parse_error && (
                <div className="p-3 border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 rounded-lg text-xs text-amber-800 dark:text-amber-300">
                  A Vessel Asset Inventory sheet was found, but 0 importable rows were extracted
                  (template example rows and empty rows are skipped). Nothing will be added to the inventory.
                </div>
              )}

              {/* Existing match — auto-decided, override behind "Change" */}
              {parseResult.existing_profile && (
                <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Ship className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-slate-900 dark:text-white">
                        {reuseChoice === 'reuse'
                          ? parseResult.existing_profile.profile_empty && !parseResult.existing_profile.contents_match
                            ? 'Existing profile is empty — reusing it'
                            : 'Identical to the existing profile — reusing it'
                          : 'Content differs — creating a separate profile'}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        {reuseChoice === 'reuse' ? (
                          <>This vessel will be mapped to &ldquo;{parseResult.existing_profile.vendor_name} / {parseResult.existing_profile.model_name}&rdquo; (used on {parseResult.existing_profile.linked_count} system{parseResult.existing_profile.linked_count !== 1 ? 's' : ''}).</>
                        ) : (
                          <>&ldquo;{parseResult.existing_profile.vendor_name} / {parseResult.existing_profile.model_name}&rdquo; stays untouched; this vessel gets its own profile.</>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowChoiceOverride(v => !v)}
                      className="shrink-0 text-[11px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    >
                      {showChoiceOverride ? t('wordRa.hide') : t('common.edit')}
                    </button>
                  </div>
                  {showChoiceOverride && (
                    <div className="mt-2.5 space-y-2 border-t border-slate-100 pt-2.5 dark:border-slate-700">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="radio" name="reuse" checked={reuseChoice === 'reuse'}
                               onChange={() => setReuseChoice('reuse')} className="mt-0.5" />
                        <div>
                          <div className="text-xs font-medium">Reuse existing profile</div>
                          <div className="text-[10px] text-slate-500">
                            Updates CBS/responses for this system AND all other vessels using this profile. New uploaded data overwrites existing.
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="radio" name="reuse" checked={reuseChoice === 'new'}
                               onChange={() => setReuseChoice('new')} className="mt-0.5" />
                        <div>
                          <div className="text-xs font-medium">Create separate profile</div>
                          <div className="text-[10px] text-slate-500">
                            This variant is different. Keep the existing profile untouched; create a new one for this vessel only.
                          </div>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <Button variant="secondary" onClick={onClose} disabled={committing}>{t('common.cancel')}</Button>
          {parseResult && (
            <Button onClick={handleCommit} disabled={!reuseChoice || committing}>
              {committing ? t('wordRa.saving') : t('common.save')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
